import { EmojiAdornment } from '@app/modules/fragments/screens/add/EmojiAdornment';
import { EmojiPickerPosition } from '@app/modules/fragments/screens/add/emoji-picker-position';
import { EmojiSelectionRange } from '@app/modules/fragments/screens/add/EmojiPickerButton';
import { insertTextAtSelection } from '@app/modules/fragments/screens/add/insert-text-at-selection';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { TextFieldElement, TextFieldElementProps } from 'react-hook-form-mui';

type TextFieldElementWithEmojiProps<TFieldValues extends FieldValues = FieldValues> =
  TextFieldElementProps<TFieldValues> & {
    /** Where the picker opens relative to the input. Defaults to `left`. */
    emojiPosition?: EmojiPickerPosition;
  };

/**
 * TextFieldElement with an emoji picker end adornment that inserts the selected
 * emoji at the text caret (same behavior as fragment title/description).
 */
export function TextFieldElementWithEmoji<TFieldValues extends FieldValues = FieldValues>({
  name,
  emojiPosition = 'right',
  InputProps,
  multiline,
  ...props
}: TextFieldElementWithEmojiProps<TFieldValues>) {
  const { getValues, setValue } = useFormContext<TFieldValues>();

  /** Inserts an emoji at the caret (or replaces the current selection) in this field. */
  function insertEmoji(emoji: string, selection: EmojiSelectionRange) {
    const fieldName = name as Path<TFieldValues>;
    const current = getValues(fieldName);
    const value = typeof current === 'string' ? current : '';
    setValue(
      fieldName,
      insertTextAtSelection(value, emoji, selection.start, selection.end) as never,
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }

  return (
    <TextFieldElement
      name={name}
      multiline={multiline}
      {...props}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            <EmojiAdornment
              onSelect={insertEmoji}
              position={emojiPosition}
              // Multiline: pin to the first line. Single-line: vertically center.
              align={multiline ? 'start' : 'center'}
            />
          </>
        ),
      }}
    />
  );
}
